/**
 * 批量导入结果归一化：优先结构化 response，兼容旧版字符串 msg。
 * 最后更新：2026-09-02
 */

function normalizeFailureItem(item) {
  if (!item) return { rowIndex: 0, category: '失败', message: '', key: '' };
  const rIdx = item.rowIndex != null
    ? item.rowIndex
    : (item.RowIndex != null
      ? item.RowIndex
      : (item.rowNumber != null
        ? item.rowNumber
        : (item.RowNumber != null ? item.RowNumber : 0)));
  return {
    rowIndex: rIdx,
    category: item.category || item.Category || '失败',
    message: item.message || item.Message || '',
    key: item.key || item.Key || ''
  };
}

function normalizeBatchBody(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return null;
  }
  const failures = body.failures || body.Failures || body.errors || body.Errors;
  if (!Array.isArray(failures)) {
    return null;
  }
  const successCount = body.successCount != null ? body.successCount : (body.SuccessCount != null ? body.SuccessCount : 0);
  const failCount = body.failCount != null ? body.failCount : (body.FailCount != null ? body.FailCount : failures.length);
  const totalCount = body.totalCount != null ? body.totalCount : (body.TotalCount != null ? body.TotalCount : (successCount + failCount));
  return {
    totalCount: totalCount,
    successCount: successCount,
    failCount: failCount,
    insertCount: body.insertCount != null ? body.insertCount : (body.InsertCount != null ? body.InsertCount : 0),
    updateCount: body.updateCount != null ? body.updateCount : (body.UpdateCount != null ? body.UpdateCount : 0),
    summary: body.summary || body.Summary || '',
    items: failures.map(normalizeFailureItem)
  };
}

/**
 * @param {object} resData axios res.data
 * @returns {{ summary: string, items: Array, stats: object|null }}
 */
export function normalizeImportBatchResponse(resData) {
  if (!resData) {
    return { summary: '', items: [], stats: null };
  }

  const structured = normalizeBatchBody(resData.response);
  if (structured) {
    return {
      summary: structured.summary || resData.msg || '',
      items: structured.items,
      stats: {
        totalCount: structured.totalCount,
        successCount: structured.successCount,
        failCount: structured.failCount,
        insertCount: structured.insertCount,
        updateCount: structured.updateCount
      }
    };
  }

  const legacyMsg = typeof resData.response === 'string'
    ? resData.response
    : (resData.msg || '');
  return parseImportResultMessage(legacyMsg);
}

/**
 * @param {object} batchResult 直接传入 ImportBatchResultDto
 */
export function normalizeImportBatchResult(batchResult) {
  const structured = normalizeBatchBody(batchResult);
  if (!structured) {
    return { summary: '', items: [], stats: null };
  }
  return {
    summary: structured.summary,
    items: structured.items,
    stats: {
      totalCount: structured.totalCount,
      successCount: structured.successCount,
      failCount: structured.failCount,
      insertCount: structured.insertCount,
      updateCount: structured.updateCount
    }
  };
}

const SECTION_MARKERS = [
  { category: '失败', token: '，失败：', split: /，/ },
  { category: '未找到教师', token: '。以下教师在本校内未找到：', split: /，/ },
  { category: '未找到教师', token: '以下教师在本校内未找到：', split: /，/ },
  { category: '部门未定位', token: '。以下部门未定位成功：', split: /，/ },
  { category: '部门未定位', token: '以下部门未定位成功：', split: /，/ },
  { category: '学校未找到', token: '。以下学校未找到：', split: /，/ },
  { category: '学校未找到', token: '以下学校未找到：', split: /，/ },
  { category: '学校未找到', token: '。未找到学校：', split: /[、，]/ },
  { category: '学校未找到', token: '未找到学校：', split: /[、，]/ },
  { category: '卡号异常', token: '。卡号异常：', split: /\n/ },
  { category: '卡号异常', token: '卡号异常：', split: /\n/ }
];

function splitDetailChunk(chunk, splitPattern) {
  return String(chunk || '')
    .split(splitPattern)
    .map(s => s.trim().replace(/^[，。、\s]+|[，。、\s]+$/g, ''))
    .filter(Boolean);
}

function findSectionEnd(text, start) {
  let end = text.length;
  SECTION_MARKERS.forEach(marker => {
    const nextIdx = text.indexOf(marker.token, start);
    if (nextIdx >= 0 && nextIdx < end) {
      end = nextIdx;
    }
  });
  return end;
}

/** 兼容旧版 msg 字符串解析 */
export function parseImportResultMessage(raw) {
  const text = String(raw || '').trim();
  if (!text) {
    return { summary: '', items: [], stats: null };
  }

  const items = [];
  let firstDetailPos = text.length;

  SECTION_MARKERS.forEach(marker => {
    const idx = text.indexOf(marker.token);
    if (idx >= 0 && idx < firstDetailPos) {
      firstDetailPos = idx;
    }
  });

  const summary = (firstDetailPos < text.length ? text.substring(0, firstDetailPos) : text)
    .replace(/[，。]$/, '')
    .trim();

  SECTION_MARKERS.forEach(marker => {
    let searchFrom = 0;
    while (searchFrom < text.length) {
      const idx = text.indexOf(marker.token, searchFrom);
      if (idx < 0) {
        break;
      }
      const start = idx + marker.token.length;
      const end = findSectionEnd(text, start);
      const chunk = text.substring(start, end);
      splitDetailChunk(chunk, marker.split).forEach(message => {
        items.push({ rowIndex: 0, category: marker.category, message, key: '' });
      });
      searchFrom = start;
    }
  });

  return {
    summary,
    items,
    stats: items.length ? { failCount: items.length } : null
  };
}
